import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";

/**
 * Gymkee 2027 modal (spec modal 1.7; sheet DS13). The anatomy is coach web's, cut to the one
 * dialog this app opens: the cancel confirmation.
 *
 * The light scrim dims Gymkee without hiding it. The container is paper at radius card with a
 * hairline and the overlay elevation, 520 wide and never growing with the window, centred when
 * it is shorter than the viewport. The header carries the title at 20 in the display face and
 * the one line under it naming the money and the date; the footer sits on paper soft with a
 * hairline above it, the safe way out on the left and the action that commits on the right.
 *
 * There is no close glyph, because this is a confirmation: `alertdialog` interrupts, the body is
 * the consequence, and the named ghost in the footer is the way out (modal 1.7 doNot 2).
 *
 * Escape closes it, the scrim closes it, focus moves to the dialog itself on open so the title
 * and the body are announced, the first Tab lands on the safe way out (never on the destructive
 * one, button-destructive 1.5 doNot 5), Tab is trapped inside, and focus returns to the control
 * that opened it on close.
 */

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** The two actions, safe first; the row is laid out by the footer. */
  footer: ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
}: ModalProps) {
  /*
   * The dialog is held in the tree for its own exit.
   *
   * `.ds-rise` is a transition between two states, so a dialog that mounts already open animates
   * nothing and one that unmounts on close is gone before its exit can run. It mounts closed,
   * flips to open on the next frame, and on close goes back to closed and is dropped a fast
   * duration later, which is what the class was written for.
   */
  const [mounted, setMounted] = useState(open);
  const [state, setState] = useState<"open" | "closed">("closed");

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = window.requestAnimationFrame(() => setState("open"));
      return () => window.cancelAnimationFrame(frame);
    }
    setState("closed");
    const timer = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const ids = useId();
  const titleId = `${ids}-title`;
  const bodyId = `${ids}-body`;

  useEffect(() => {
    if (!open || !mounted) return;
    triggerRef.current = document.activeElement;
    /* Focus lands on the DIALOG, not on the safe action.
       modal 1.7 asks for the safe way out to be where focus goes, and focusing the ghost button
       satisfied that and drew the orange ring on it every time the dialog opened — a ring on a
       control nobody had pressed, on a page whose one orange is elsewhere. The container is
       `tabindex="-1"` and `aria-labelledby`/`aria-describedby` the title and the body, so a
       screen reader reads the question and the consequence on open, and the first Tab still
       lands on the ghost because it is the first focusable inside. The ring then appears
       because the reader asked for it. */
    panelRef.current?.focus();
    return () => {
      const trigger = triggerRef.current;
      if (trigger instanceof HTMLElement && document.contains(trigger))
        trigger.focus();
    };
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (
        event.shiftKey &&
        (active === first || !panelRef.current.contains(active))
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      className="ds-scrim ds-pay-scrim ds-fade"
      data-state={state}
      onMouseDown={onClose}
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
        data-state={state}
        className="ds-pay-lede ds-card ds-overlay ds-rise flex max-h-[calc(100dvh-32px)] w-full max-w-[520px] flex-col overflow-hidden"
      >
        <div className="px-gk-6 pt-gk-6 pb-gk-4 flex flex-col gap-gk-1">
          <h2
            id={titleId}
            className="text-[20px] font-semibold text-[var(--gk-text-primary)]"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-[14px] text-[var(--gk-text-secondary)]">
              {subtitle}
            </p>
          )}
        </div>
        <div
          id={bodyId}
          className="px-gk-6 pb-gk-6 gap-gk-4 flex min-h-0 flex-1 flex-col overflow-y-auto text-[15px] leading-[1.55] text-[var(--gk-text-secondary)]"
        >
          {children}
        </div>
        <div className="bg-surface-paperSoft ds-hairline px-gk-6 py-gk-4 gap-gk-3 flex flex-wrap items-center justify-end border-t max-sm:flex-col max-sm:items-stretch">
          {footer}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** motion.duration.fast: the exit, shorter than the entrance, as every exit is. */
const EXIT_MS = 120;
